/**
 * IBD Custom Function
 * Replaces: InSyncAddinMethod.cs ISD() method (lines 464-560)
 * Original: ISD() function renamed to IBD for clarity (InSync Broker Data)
 */

import { DataStore } from './dataStore';
// Date helpers are shared with IAD rather than duplicated: both build keys for
// the same backend, so a second parser is a second set of bugs.
import { excelDateSerial, excelSerialToDate, parseCellDate } from './IAD';
import { createLogger } from '../utils/logger';
import { tokenStorage } from '../core/auth/tokenStorage';
import { recordAddinActivity } from '../core/auth/idleSession';
import { api } from '../core/api/endpoints';
import { devLog } from '../utils/devLog';

const logger = createLogger('IBD');
const pendingRequests = new Map<string, Promise<any>>();

/**
 * The `datedon` argument as the key's 3rd field: `yyyy-MM-dd`, or "" if it is not
 * a date.
 *
 * Two traps this has to survive, both already solved in IAD.ts — the helpers are
 * imported rather than reimplemented so the two files cannot drift again:
 *
 *  - EXCEL DATE CELLS. `=ISD(B8,"stock_price",$G$5)` where G5 holds 1/1/2026
 *    does NOT pass "1/1/2026". Excel hands a custom function the date's SERIAL,
 *    and `datedon` is declared `string` in functions.json, so the value arrives
 *    already stringified as "46023". The old `new Date("46023")` did not fail —
 *    V8 read it as YEAR 46023 — producing
 *      AAPL|CLOSE|46023-01-01|STOCK|||   (backend has no such row -> "-")
 *    instead of the working
 *      AAPL|CLOSE|2026-01-01|STOCK|||    (-> 271.86)
 *
 *  - NON-DATE TEXT. COM's `DateTime.TryParse(datedon, ...) ? ... : ""` discards
 *    anything unparseable, so a stray "E"/"A"/label cannot leak into the key.
 *    The old code returned the raw text instead, poisoning the key.
 */
function normalizeIsdDate(value?: any): string {
  const serial = excelDateSerial(value);
  const date = serial !== null ? excelSerialToDate(serial) : parseCellDate(String(value ?? '').trim());
  if (!date) {
    // Matches COM: unparseable -> "" (fall back to latest), never the raw text.
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * The ISD cache key. Exported so the shape can be asserted directly — verified
 * live 2026-08-21: `AAPL|CLOSE|2026-01-01|STOCK|||` returns 271.86.
 */
export function buildIsdKey(ticker: string, datacategory: string, datedon?: any): string {
  return [
    ticker,
    datacategory,
    normalizeIsdDate(datedon),
    'Stock',
    '',
    '',
    '',
  ].join('|').toUpperCase();
}

// ============================================================================
// IBD TIMING TRACKING
// ============================================================================
let ibdSessionStartTime: number | null = null;
let totalIbdRequests = 0;
let totalIbdFetches = 0;
let ibdSessionActive = false;

function startIbdSession(): void {
  if (!ibdSessionActive) {
    ibdSessionStartTime = performance.now();
    totalIbdRequests = 0;
    totalIbdFetches = 0;
    ibdSessionActive = true;
    devLog(`[IBD] [TIMING] Session started at ${new Date().toISOString()}`);
  }
}

function endIbdSession(): void {
  if (ibdSessionActive && ibdSessionStartTime) {
    const totalTime = performance.now() - ibdSessionStartTime;
    devLog(`[IBD] [TIMING] ========== SESSION COMPLETE ==========`);
    devLog(`[IBD] [TIMING] Total session time: ${totalTime.toFixed(2)}ms`);
    devLog(`[IBD] [TIMING] Total requests: ${totalIbdRequests}`);
    devLog(`[IBD] [TIMING] Unique fetches (deduped): ${totalIbdFetches}`);
    devLog(`[IBD] [TIMING] =========================================`);
    ibdSessionActive = false;
    ibdSessionStartTime = null;
  }
}

// Track in-flight count to detect when session ends
let inFlightIbdCount = 0;
let ibdEndTimeout: ReturnType<typeof setTimeout> | undefined;

/**
 * InSync Broker Data - Retrieves broker-specific market data.
 * @customfunction IBD_OFJ
 * @helpurl https://insyncanalytics.com/consensus/add-in/functions/#ibd-ofj
 * @volatile
 * @param ticker Ticker symbol.
 * @param datacategory Data category or broker selector.
 * @param datedon Date or datapoint selector.
 * @param ltType Optional broker subtype.
 * @returns Requested broker data value.
 */
export async function IBD_OFJ(
  ticker: string,
  datacategory: string,
  datedon?: string,
  ltType?: string
): Promise<any> {
  // Evaluating an InSync formula counts as using the add-in (OTP idle timeout).
  recordAddinActivity();
  startIbdSession();
  totalIbdRequests++;
  inFlightIbdCount++;
  // Clear any pending end session timer
  if (ibdEndTimeout) {
    clearTimeout(ibdEndTimeout);
    ibdEndTimeout = undefined;
  }
  
  logger.enter('IBD', { ticker, datacategory, datedon, ltType });
  
  // Normalize inputs
  const t = (ticker || '').toUpperCase();
  let category = (datacategory || '').toLowerCase();
  const date = datedon || '';

  // Map legacy category names
  if (category === 'stock_price') {
    category = 'close';
  }

  // Match COM ISD exactly: Ticker|DataCategory|Date|Stock|||
  const fullKey = buildIsdKey(t, category, date);
  logger.debug('Built IBD key', { fullKey });

  // 1. Check cache hit
  const cachedValue = DataStore.get(fullKey);
  if (cachedValue !== undefined) {
    logger.debug('Cache hit', { fullKey, value: cachedValue });
    inFlightIbdCount--;
    checkIbdSessionEnd();
    const result = formatIBDValue(cachedValue, category);
    logger.exit('IBD', { result, source: 'cache' });
    return result;
  }

  const pendingRequest = pendingRequests.get(fullKey);
  if (pendingRequest) {
    logger.debug('Awaiting in-flight request', { fullKey });
    const result = await pendingRequest;
    inFlightIbdCount--;
    checkIbdSessionEnd();
    logger.exit('IBD', { result, source: 'in_flight' });
    return result;
  }

  logger.info('Cache miss - fetching value in custom functions runtime', { fullKey });
  totalIbdFetches++;
  const request = fetchAndCacheValue(fullKey, category);
  pendingRequests.set(fullKey, request);

  try {
    const result = await request;
    inFlightIbdCount--;
    checkIbdSessionEnd();
    logger.exit('IBD', { result, source: 'api' });
    return result;
  } finally {
    pendingRequests.delete(fullKey);
  }
}

function checkIbdSessionEnd(): void {
  if (inFlightIbdCount === 0 && ibdSessionActive) {
    // Delay session end to catch any straggler calls
    ibdEndTimeout = setTimeout(() => {
      if (inFlightIbdCount === 0) {
        endIbdSession();
      }
    }, 500);
  }
}

async function fetchAndCacheValue(fullKey: string, category: string): Promise<any> {
  const fetchStartTime = performance.now();
  try {
    const storedToken = await tokenStorage.getAuthToken();
    // Per-user token when available, else the shared env TEMP_TOKEN fallback.
    const usernameFilter = await tokenStorage.getUsernameFilter();
    if (!usernameFilter) {
      logger.warn('No auth token available for custom function fetch');
      return '#AUTH!';
    }

    if (!storedToken) {
      logger.warn('Using TEMP_TOKEN fallback for custom function fetch; no logged-in token found');
    }

    const response = await api.batch.getBatchData([fullKey], usernameFilter);
    const fetchTime = performance.now() - fetchStartTime;
    const matched = response.data?.find((item) => item.Key === fullKey);

    devLog(`[IBD] [TIMING] Fetch completed: ${fetchTime.toFixed(2)}ms for key ${fullKey.slice(0, 50)}...`);

    if (!matched) {
      logger.warn('No batch result returned for key', { fullKey });
      return '';
    }

    DataStore.set(fullKey, matched.Value);
    return formatIBDValue(matched.Value, category);
  } catch (error) {
    const failTime = performance.now() - fetchStartTime;
    console.error(`[IBD] [TIMING] Fetch failed after ${failTime.toFixed(2)}ms:`, error);
    logger.error('Failed to fetch IBD value', error as Error, { fullKey });
    return '#ERROR';
  }
}

/**
 * Format IBD value for Excel
 */
function formatIBDValue(value: any, _category: string): any {
  if (value === null || value === undefined) {
    return null;
  }

  // Handle string
  if (typeof value === 'string') {
    if (value === '-' || value.startsWith('Error:')) {
      return value;
    }

    // Try parse as number
    const num = parseFloat(value);
    if (!isNaN(num) && isFinite(num)) {
      return num;
    }

    return value;
  }

  // Handle numbers
  if (typeof value === 'number') {
    return value;
  }

  // Handle dates
  if (value instanceof Date) {
    return value;
  }

  return value;
}

/**
 * IBD2 - Legacy direct API call variant
 * For immediate results without background fetching
 */
export async function IBD2(
  ticker: string,
  datacategory: string,
  datedon?: string,
  ltType?: string
): Promise<any> {
  return IBD_OFJ(ticker, datacategory, datedon, ltType);
}

/**
 * InSync Stock Data - COM-compatible ISD formula alias.
 * Keeps the legacy Insert Formula stock output shape while reusing IBD_OFJ.
 * @customfunction ISD_OFJ
 * @helpurl https://insyncanalytics.com/consensus/add-in/functions/#isd-ofj
 * @streaming
 * @param ticker Ticker symbol.
 * @param datacategory Stock data category.
 * @param datedon Optional date selector.
 * @param ltType Optional subtype.
 * @param {CustomFunctions.StreamingInvocation<any>} invocation Streaming invocation used to update the cell.
 */
export function ISD_OFJ(
  ticker: string,
  datacategory: string,
  datedon?: string,
  ltType?: string,
  invocation?: CustomFunctions.StreamingInvocation<any>
): void {
  if (!invocation?.setResult) {
    console.warn('[ISD] Streaming invocation was not provided by Excel.');
    return;
  }

  // Evaluating an InSync formula counts as using the add-in (OTP idle timeout).
  recordAddinActivity();

  let canceled = false;
  invocation.setResult('Loading...');
  invocation.onCanceled = () => {
    canceled = true;
  };

  IBD_OFJ(ticker, datacategory, datedon, ltType)
    .then((result) => {
      if (!canceled) {
        invocation.setResult(result);
      }
    })
    .catch((error) => {
      console.error('[ISD] ERROR processing streaming request:', error);
      logger.error('ISD streaming function error', error as Error);
      if (!canceled) {
        invocation.setResult('#ERROR');
      }
    });
}

export default IBD_OFJ;

CustomFunctions.associate("IBD_OFJ", IBD_OFJ);
CustomFunctions.associate("ISD_OFJ", ISD_OFJ);